import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';

interface FeedbackWidgetProps {
  executionId: string;
  existingRating?: number | null;
  existingNote?: string | null;
  onSubmitted?: (rating: number, note: string | null) => void;
}

export const FeedbackWidget = ({
  executionId,
  existingRating,
  existingNote,
  onSubmitted,
}: FeedbackWidgetProps) => {
  const [rating, setRating] = useState<number>(existingRating ?? 0);
  const [hovered, setHovered] = useState(0);
  const [note, setNote] = useState(existingNote ?? '');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(Boolean(existingRating));

  const handleSubmit = async () => {
    if (rating === 0) return;
    setSaving(true);

    const { error } = await supabase
      .from('flow_executions')
      .update({
        rating,
        feedback_note: note.trim() || null,
      })
      .eq('id', executionId);

    setSaving(false);
    if (!error) {
      setSubmitted(true);
      onSubmitted?.(rating, note.trim() || null);
    }
  };

  if (submitted && !existingRating) {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200 text-center">
        <p className="text-sm text-gray-600">Merci pour votre retour !</p>
        <div className="flex justify-center gap-0.5 mt-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <StarIcon key={star} filled={star <= rating} />
          ))}
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-gray-500">Votre note</p>
          <button
            onClick={() => setSubmitted(false)}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Modifier
          </button>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <StarIcon key={star} filled={star <= rating} />
            ))}
          </div>
          {existingNote && (
            <span className="text-xs text-gray-500 truncate">{existingNote}</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200">
      <p className="text-xs font-medium text-gray-700 mb-2">
        Comment s'est passée cette exécution ?
      </p>
      <div className="flex gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => setRating(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            className="p-0.5 transition-transform hover:scale-110"
          >
            <StarIcon filled={star <= (hovered || rating)} />
          </button>
        ))}
      </div>
      {rating > 0 && (
        <>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Un commentaire ? (optionnel)"
            rows={2}
            className="text-xs mb-2"
          />
          <div className="flex justify-end">
            <Button size="sm" onClick={handleSubmit} loading={saving}>
              Envoyer
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill={filled ? '#111827' : 'none'}
    stroke="#111827"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);
